/**
 * LivePricingService
 *
 * Orchestrates exchange WebSocket adapters. Call `subscribe()` to start
 * streaming real-time price updates for a token on a given exchange.
 *
 * futuresOnly = true  → use the futures WS; return error if token not available
 * futuresOnly = false → try futures first, fall back to spot WS if needed
 */

import type {
  ExchangeCode,
  TokenAvailability,
} from "../market-data/market-data.types";
import type {
  ExchangeWsAdapter,
  PriceErrorCallback,
  PriceUpdateCallback,
  SubscriptionOptions,
} from "./live-pricing.types";

import {
  binanceFuturesWsAdapter,
  binanceSpotWsAdapter,
} from "./adapters/binance-ws";
import {
  bybitLinearWsAdapter,
  bybitSpotWsAdapter,
} from "./adapters/bybit-ws";
import { kucoinFuturesWsAdapter } from "./adapters/kucoin-ws";
import { gateFuturesWsAdapter, gateSpotWsAdapter } from "./adapters/gate-ws";
import { hyperliquidWsAdapter } from "./adapters/hyperliquid-ws";
import { asterdexWsAdapter } from "./adapters/asterdex-ws";

interface AdapterPair {
  futures: ExchangeWsAdapter;
  spot?: ExchangeWsAdapter;
}

const ADAPTERS: Record<ExchangeCode, AdapterPair> = {
  binance: { futures: binanceFuturesWsAdapter, spot: binanceSpotWsAdapter },
  bybit: { futures: bybitLinearWsAdapter, spot: bybitSpotWsAdapter },
  kucoin: { futures: kucoinFuturesWsAdapter },
  gate: { futures: gateFuturesWsAdapter, spot: gateSpotWsAdapter },
  hyperliquid: { futures: hyperliquidWsAdapter },
  asterdex: { futures: asterdexWsAdapter },
};

export class LivePricingService {
  subscribe(
    exchange: ExchangeCode,
    symbol: string,
    availability: TokenAvailability,
    onUpdate: PriceUpdateCallback,
    onError?: PriceErrorCallback,
    options?: SubscriptionOptions
  ): () => void {
    const futuresOnly = options?.futuresOnly ?? true;
    const pair = ADAPTERS[exchange];

    if (availability.available) {
      return pair.futures.subscribe(symbol, onUpdate, onError);
    }

    if (!futuresOnly && availability.spotAvailable && pair.spot) {
      return pair.spot.subscribe(symbol, onUpdate, onError);
    }

    onError?.(
      new Error(
        `${symbol} is not available on ${exchange} (${futuresOnly ? "futures" : "futures or spot"})`
      )
    );
    return () => {};
  }
}

let _instance: LivePricingService | null = null;
export function getLivePricingService(): LivePricingService {
  if (!_instance) _instance = new LivePricingService();
  return _instance;
}
